import { DataTable } from '@creditornot/cb-data-table';
import { Switch } from '@creditornot/cb-switch';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  subscribeToStatusInOrderApiCredentials,
  unSubscribeToStatusInOrderApiCredentials,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/api';
import {
  OrderApiNotificationStatus,
  OrderApiWebhookData,
} from 'apis/restaurant-api/merchant-admin/order-api-credentials/types';

type Props = {
  merchantId: string;
  webhookMetadata: OrderApiWebhookData;
};

type WebhookStatusValue = {
  id: OrderApiNotificationStatus;
  name: string;
  enabled: boolean;
};

const SubscriptionSwitch = styled.div`
  display: flex;
  justify-content: end;
  margin-inline-end: 8px;
`;

const notificationStatusTranslationMap: Record<OrderApiNotificationStatus, string> = {
  CREATED: 'merchant-integrations.order-api.events.created',
  PRODUCTION: 'merchant-integrations.order-api.events.production',
  READY: 'merchant-integrations.order-api.events.ready',
  DELIVERED: 'merchant-integrations.order-api.events.delivered',
  CANCELED: 'merchant-integrations.order-api.events.canceled',
};

const OrderApiEventSubscription = ({ merchantId, webhookMetadata }: Props) => {
  const intl = useIntl();
  const [webhookStatusValues, setWebhookStatusValues] = useState<WebhookStatusValue[]>([]);

  useEffect(() => {
    const orderApiEventsWithSubscription = Object.values(OrderApiNotificationStatus).map(
      (status: OrderApiNotificationStatus) => ({
        id: status,
        name: status.toString(),
        enabled: webhookMetadata ? !!webhookMetadata.subscribed_statuses.find(s => s === status) : false,
      }),
    );
    setWebhookStatusValues(orderApiEventsWithSubscription);
  }, [webhookMetadata]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubscriptionChange = (webhook: WebhookStatusValue) => {
    const updatedVenues = webhookStatusValues.map((v: WebhookStatusValue) => {
      if (v.id !== webhook.id) {
        return v;
      }
      return {
        ...v,
        enabled: !webhook.enabled,
      };
    });

    const payload = { statuses: [webhook.id] };
    if (webhook.enabled) {
      unSubscribeToStatusInOrderApiCredentials(merchantId, webhookMetadata.credential_id, payload);
    } else {
      subscribeToStatusInOrderApiCredentials(merchantId, webhookMetadata.credential_id, payload);
    }
    setWebhookStatusValues(updatedVenues);
  };

  return (
    <DataTable
      dataSource={webhookStatusValues}
      getRowKey={record => record.id.toString()}
      horizontalPadding="wide"
      columns={[
        {
          id: 'event',
          name: intl.formatMessage({ id: 'merchant-integrations.order-api.webhook.event-title' }),
          render: ({ record: webhook }) => <FormattedMessage id={notificationStatusTranslationMap[webhook.id]} />,
        },
        {
          id: 'enabled',
          name: intl.formatMessage({ id: 'merchant-integrations.enabled' }),
          renderName: false,
          render: ({ record: webhook }) => (
            <SubscriptionSwitch>
              <Switch checked={webhook.enabled} onChange={() => onSubscriptionChange(webhook)} />
            </SubscriptionSwitch>
          ),
        },
      ]}
    />
  );
};

export default OrderApiEventSubscription;
